/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { TableOutlined } from '@ant-design/icons'
import { Checkbox, Popover } from 'antd'
import { useState } from 'react'

import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import {
  font,
  paragraphSmall,
  weightMedium,
  weightSemiBold,
} from 'src/components/css'

/** One toggleable column, as the chooser renders it. */
export interface ColumnChooserItem {
  colId: string
  headerName: string
  visible: boolean
}

export interface ColumnChooserProps {
  /** Columns to offer, in display order. */
  columns: ColumnChooserItem[]
  onToggle: (colId: string, visible: boolean) => void
  onReset: () => void
  /** Button label, e.g. "Team Columns". */
  label: string
  /** Panel heading. Defaults to the label. */
  title?: string
  disabled?: boolean
  'data-testid'?: string
}

/**
 * Column show/hide popover.
 *
 * Takes plain `{ colId, headerName, visible }` items and reports toggles back,
 * so it does not care where the state lives or how a consumer applies it —
 * antd's `hidden` on a column def and a plain render guard are both fine. That
 * is what lets one component serve the two tables and the division header,
 * which has no column defs at all.
 *
 * @example
 * const chooser = useColumnVisibility({ columns, storageKey })
 * <ColumnChooser
 *   label='Team Columns'
 *   columns={chooser.columns}
 *   onToggle={chooser.setColumnVisible}
 *   onReset={chooser.resetColumns}
 * />
 */
const ColumnChooser = ({
  columns,
  onToggle,
  onReset,
  label,
  title,
  disabled = false,
  'data-testid': testId = 'column-chooser',
}: ColumnChooserProps) => {
  'use memo'

  const [open, setOpen] = useState(false)

  const hiddenCount = columns.filter((column) => !column.visible).length

  const panel = (
    <div css={panelStyles} data-testid={`${testId}-panel`}>
      <p css={panelTitleStyles}>{capitalizeWords(title ?? label)}</p>
      <div css={panelListStyles}>
        {columns.map((column) => (
          <Checkbox
            key={column.colId}
            checked={column.visible}
            onChange={(event) => onToggle(column.colId, event.target.checked)}
          >
            <span css={checkboxLabelStyles}>
              {capitalizeWords(column.headerName)}
            </span>
          </Checkbox>
        ))}
      </div>
      <div css={panelActionsStyles}>
        <button
          type='button'
          css={resetButtonStyles}
          data-testid={`${testId}-reset`}
          onClick={onReset}
        >
          Reset
        </button>
      </div>
    </div>
  )

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger='click'
      placement='bottomRight'
      content={panel}
    >
      <button
        type='button'
        css={triggerStyles}
        data-testid={testId}
        aria-expanded={open}
        disabled={disabled}
      >
        <TableOutlined aria-hidden='true' />
        {capitalizeWords(label)}
        {/* The count is the only cue that a table is not showing everything. */}
        {hiddenCount > 0 ? <span css={badgeStyles}>{hiddenCount}</span> : null}
      </button>
    </Popover>
  )
}

export default ColumnChooser

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

/*
 * Matches the registration filter row's own controls — Expand All and the
 * Only Drafts / Only Deferred Payments switches
 * (`pages/registration/components/SearchControls`): filled SECONDARY_300 on a
 * SECONDARY_200 border, 40px, and a PRIMARY border on hover rather than a
 * fill change. Keeping the fill is what makes these read as part of that row
 * instead of as ghost buttons floating beneath it.
 */
const triggerStyles = css`
  ${font.body}
  ${paragraphSmall}
  ${weightMedium}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_200};
  background: ${colors.SECONDARY_300};
  color: ${colors.NEUTRAL_0};
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: ${colors.PRIMARY};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 2px;
  }
`

const badgeStyles = css`
  ${weightSemiBold}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: ${colors.PRIMARY};
  /* Text on a PRIMARY fill: tenant-switched, so it stays legible on Canlan. */
  color: ${PRIMARY_BUTTON_CONTENT.text};
  font-size: 10px;
  line-height: 1;
`

/*
 * The panel matches the game list's column chooser
 * (`features/game/gameListView/components/GridToolbar.tsx`) so the two read as
 * one control. Keep them in step — if this changes, that one should too.
 */
const panelStyles = css`
  ${font.body}
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 200px;
`

const panelTitleStyles = css`
  ${paragraphSmall}
  ${weightSemiBold}
  margin: 0;
  color: ${colors.NEUTRAL_0};
`

const panelListStyles = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
`

const checkboxLabelStyles = css`
  ${paragraphSmall}
  color: ${colors.NEUTRAL_0};
`

const panelActionsStyles = css`
  display: flex;
  justify-content: flex-end;
  padding-top: 8px;
  border-top: 1px solid ${colors.SECONDARY_200};
`

/* Left matching the game list's panel — see the note on `panelStyles`. */
const resetButtonStyles = css`
  ${font.body}
  ${paragraphSmall}
  height: 32px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: transparent;
  color: ${colors.NEUTRAL_0};
  cursor: pointer;

  &:hover {
    background: ${colors.SECONDARY_300};
  }
`
